import { useCallback, useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MdClose } from "react-icons/md";
import {
  EditorContext,
  ScratchEnvironmentContext,
} from "components/utils/Contexts";
import { getMask } from "./utils";

interface OnboardingInstructionsProps {
  scratchIframe: React.MutableRefObject<HTMLIFrameElement | null>;
  setShowOnboarding: React.Dispatch<React.SetStateAction<boolean>>;
  afterFinishingFunction?: () => void;
}

const maskInfo: {
  [key: number]: {
    id?: string;
    shape: "circle" | "ellipse" | "rect";
    radius?: number;
    postMessage?: string;
    topShift: number;
    leftShift: number;
    extraWidth: number;
    extraHeight: number;
    borderRadius?: number;
  };
} = {
  1: {
    id: "ai-assistant-button",
    shape: "circle",
    radius: 60,
    topShift: 0,
    leftShift: 0,
    extraWidth: 0,
    extraHeight: 0,
  },
  2: {
    id: "scratch-edit-button",
    shape: "circle",
    radius: 60,
    topShift: 0,
    leftShift: -6,
    extraWidth: 0,
    extraHeight: 0,
  },
  3: {
    id: "scratch-file-button",
    shape: "circle",
    radius: 60,
    topShift: 0,
    leftShift: -6,
    extraWidth: 0,
    extraHeight: 0,
  },
  4: {
    id: "scratch-back-button",
    shape: "circle",
    radius: 50,
    topShift: 0,
    leftShift: 0,
    extraWidth: 0,
    extraHeight: 0,
  },
  5: {
    postMessage: "GET_TOOLBAR_AND_BLOCKS",
    shape: "rect",
    topShift: 111,
    leftShift: 40,
    extraWidth: 0,
    extraHeight: 0,
    borderRadius: 10,
  },
  6: {
    postMessage: "GET_CODE_AREA",
    shape: "rect",
    topShift: 111,
    leftShift: 40,
    extraWidth: 0,
    extraHeight: 0,
    borderRadius: 10,
  },
  7: {
    postMessage: "GET_STAGE",
    shape: "rect",
    topShift: 111,
    leftShift: 40,
    extraWidth: 0,
    extraHeight: 0,
    borderRadius: 10,
  },
  8: {
    postMessage: "GET_SPRITE_GALLERY",
    shape: "rect",
    topShift: 111,
    leftShift: 40,
    extraWidth: 0,
    extraHeight: 0,
    borderRadius: 10,
  },
  9: {
    id: "scratch-video-view",
    shape: "rect",
    topShift: 0,
    leftShift: 0,
    extraWidth: 14,
    extraHeight: 14,
    borderRadius: 20,
  },
  10: {
    id: "scratch-instructions-button",
    shape: "rect",
    topShift: 0,
    leftShift: 0,
    extraWidth: 14,
    extraHeight: 14,
  },
};

const bubbleInfo: {
  [key: number]: { pos: "n" | "s" | "w" | "e"; xShift: number; yShift: number };
} = {
  1: {
    pos: "s",
    xShift: -368 / 2,
    yShift: 32,
  },
  2: {
    pos: "s",
    xShift: -368 / 2,
    yShift: 32,
  },
  3: {
    pos: "s",
    xShift: -368 / 2,
    yShift: 32,
  },
  4: {
    pos: "s",
    xShift: -368 / 2,
    yShift: 34,
  },
  5: {
    pos: "e",
    xShift: 20,
    yShift: 70,
  },
  6: {
    pos: "e",
    xShift: 20,
    yShift: 70,
  },
  7: {
    pos: "w",
    xShift: -368 - 20,
    yShift: 0,
  },
  8: {
    pos: "w",
    xShift: -368 - 20,
    yShift: 0,
  },
  9: {
    pos: "s",
    xShift: (-368 * 3) / 4,
    yShift: 24,
  },
  10: {
    pos: "s",
    xShift: -368 / 2,
    yShift: 24,
  },
};

const BUBBLE_WIDTH = 368;

const OnboardingInstructions: React.FC<OnboardingInstructionsProps> = ({
  scratchIframe,
  setShowOnboarding,
  afterFinishingFunction,
}) => {
  // States definition
  const [step, setStep] = useState(1);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [bubbleCoordinates, setBubbleCoordinates] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const [mask, setMask] = useState(
    "radial-gradient(circle 60px at 0px 0px, transparent 100%, black 100%)"
  );
  const [bubblePosition, setBubblePosition] = useState<"n" | "s" | "w" | "e">(
    "s"
  );

  // Get context parameters
  const { highlightedScratchElement, scaleResizeIframe } = useContext(
    ScratchEnvironmentContext
  );
  const { AIAssistant } = useContext(EditorContext);

  // Modify AI Assistant (temporary)

  /**
   * Add an event listener for the screen resize
   */
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /**
   * Function used to update the highlighted element based on the
   * current step (for regular elements)
   */
  useEffect(() => {
    // Check if the maskInfo is already defined for that step
    if (!maskInfo[step]) return;

    // Get the values of the element
    const {
      id,
      postMessage,
      shape,
      radius,
      topShift,
      leftShift,
      extraWidth,
      extraHeight,
      borderRadius,
    } = maskInfo[step];

    // Check if we have a post message
    if (postMessage && scratchIframe.current) {
      // Get the iframe
      const iframeWIndow = scratchIframe.current.contentWindow;
      // Send a message
      iframeWIndow && iframeWIndow.postMessage({ type: postMessage }, "*");
      // And finish function
      return;
    }

    // Get the element based on the defined parameter (id or className)
    const element = document.getElementById(id || "");

    // Check if we got the element
    if (element) {
      // Calculate and set position based on the original element
      const { top, left, width, height } = element.getBoundingClientRect();

      // Calculate the center of the mask
      const x = ["circle", "ellipse"].includes(shape)
        ? left + width / 2 + leftShift
        : shape === "rect"
        ? left
        : 0;
      const y = ["circle", "ellipse"].includes(shape)
        ? top + height / 2 + topShift
        : shape === "rect"
        ? top
        : 0;

      // Define the mask
      const maskToAssign = getMask({
        type: shape,
        radius,
        x: x - extraWidth / 2,
        y: y - extraHeight / 2,
        width: width + extraWidth,
        height: height + extraHeight,
        borderRadius,
      });

      // Set the state
      setMask(maskToAssign);

      // Check if the maskInfo is already defined for that step
      if (!bubbleInfo[step]) return;

      // Get the value of the bubble information
      const { pos, xShift, yShift } = bubbleInfo[step];

      // Define the coordinate value for each position type
      let xBubble = ["n", "s"].includes(pos)
        ? x + xShift + (["circle", "ellipse"].includes(shape) ? 0 : width / 2)
        : pos === "e"
        ? width + extraWidth + xShift
        : 0;
      let yBubble =
        pos === "s"
          ? y +
            (["circle", "ellipse"].includes(shape) ? height / 2 : height) +
            yShift
          : ["w", "e"].includes(pos)
          ? y + height / 2 + extraHeight + yShift
          : 0;

      // Correct if going beyond the boundaries
      // xBubble = xBubble - Math.max(0, xBubble + BUBBLE_WIDTH - window.innerWidth);

      // Set the bubble coordinates
      setBubbleCoordinates({ x: xBubble, y: yBubble });
      // And the position
      setBubblePosition(pos);
    }
  }, [step, windowSize]);

  /**
   * Function used to update the highlighted element based on the
   * current step (for Scratch elements)
   */
  useEffect(() => {
    // Check if the maskInfo is already defined for that step
    if (!(maskInfo[step] && maskInfo[step].postMessage)) return;

    // Get the mask info
    const {
      topShift,
      leftShift,
      shape,
      extraWidth,
      extraHeight,
      borderRadius,
    } = maskInfo[step];

    // Check if the highlightedScratchElement is not empty
    if (!highlightedScratchElement) return;

    // Get the element values of the element
    const { top, left, width, height } = highlightedScratchElement;

    // Define the mask
    const maskToAssign = getMask({
      type: shape,
      x: left + leftShift,
      y: top * (scaleResizeIframe || 1) + topShift,
      width,
      height: height * (scaleResizeIframe || 1),
      borderRadius,
    });

    // And set the state
    setMask(maskToAssign);

    // Check if the maskInfo is already defined for that step
    if (!bubbleInfo[step]) return;

    // Get the value of the bubble information
    const { pos, xShift, yShift } = bubbleInfo[step];

    // Define the coordinate value for each position type
    const xBubble = ["n", "s"].includes(pos)
      ? left + xShift
      : pos === "e"
      ? left + leftShift + width + extraWidth + xShift
      : pos === "w"
      ? left + leftShift + extraWidth + xShift
      : 0;
    const yBubble = ["n", "s"].includes(pos)
      ? top + yShift
      : ["w", "e"].includes(pos)
      ? top + height / 2 + extraHeight + yShift
      : 0;

    // Set the bubble coordinates
    setBubbleCoordinates({ x: xBubble, y: yBubble * (scaleResizeIframe || 1) });
    // And the position
    setBubblePosition(pos);
  }, [highlightedScratchElement]);

  /**
   * Function executed when clicking next
   */
  const handleNextClick = useCallback(() => {
    // Check the current step
    if (step === 10) {
      // Close the pop up
      setShowOnboarding(false);

      // And execute the after finishing onboarding function
      afterFinishingFunction && afterFinishingFunction();
    }
    // Otherwise
    else {
      // Set the following step
      setStep((currentStep) => currentStep + 1);
    }
  }, [step]);

  /**
   * Function used to coordinate the dot click on the bubble
   */
  const handleNavigationButtonsClick = useCallback(
    (itemNumber: number) => {
      return () => {
        setStep(itemNumber);
      };
    },
    [step]
  );

  return (
    <>
      <motion.div
        className="fixed inset-0 w-full h-full p-5 bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] ease-out inline-flex items-end justify-center z-10 transition-all duration-300"
        initial={{ opacity: 0, mask: mask }}
        animate={{ opacity: 1, mask: mask }}
        exit={{
          opacity: 0,
          transition: { duration: 0.1, ease: "easeOut" },
        }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        // onClick={() => setShowOnboarding(false)}
      />
      {/* Tooltip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ ...{ opacity: 1 }, ...bubbleCoordinates }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="fixed inset-0 w-[368px] h-fit p-4 rounded-xl flex-col justify-start items-start gap-[9px] inline-flex z-30"
      >
        {/* Arrow point */}
        <div
          className={`absolute ${
            bubblePosition === "n"
              ? "-bottom-3 left-1/2 -translate-x-1/2"
              : bubblePosition === "s"
              ? step === 9
                ? "-top-3 left-3/4 -translate-x-1/2"
                : "-top-3 left-1/2 -translate-x-1/2"
              : bubblePosition === "w"
              ? "-right-3 top-1/2 -translate-y-1/2"
              : bubblePosition === "e"
              ? "-left-3 top-1/2 -translate-y-1/2"
              : ""
          } w-6 h-6 rotate-45 bg-white`}
        />
        {/* Content */}
        <div
          style={{
            left: Math.min(
              0,
              window.innerWidth - (bubbleCoordinates.x + BUBBLE_WIDTH + 20)
            ),
          }}
          className={`fixed inset-0 w-full h-fit p-4 ${
            ["w", "e"].includes(bubblePosition)
              ? "top-1/2 -translate-y-1/2 "
              : ""
          }bg-white rounded-xl flex-col justify-start items-start gap-[9px] inline-flex z-30`}
        >
          <div className="self-stretch justify-start items-center gap-[9px] inline-flex">
            <div className="grow shrink basis-0 text-neutral-700 text-base font-semibold font-sans leading-[19px] select-none">
              {step === 1
                ? `Meet ${AIAssistant.name}, your coding buddy!`
                : step === 2
                ? "Oops, made a mistake? No problem!"
                : step === 3
                ? "File menu"
                : step === 4
                ? "Go back"
                : step === 5
                ? "Colour-coded magic"
                : step === 6
                ? "Building zone"
                : step === 7
                ? "Stage"
                : step === 8
                ? "Sprite gallery"
                : step === 9
                ? "Switch it up: video or code!"
                : step === 10
                ? "Instructions"
                : ""}
            </div>
            <div
              className="p-1.5 bg-neutral-100 hover:bg-neutral-200 ease-in-out duration-150 rounded-full justify-center items-center gap-2 flex cursor-pointer"
              onClick={() => setShowOnboarding(false)}
            >
              <div className="w-4 h-4 relative">
                <MdClose className="w-full h-full fill-neutral-700" />
              </div>
            </div>
          </div>
          <div className="self-stretch text-neutral-700 text-base font-normal font-sans leading-normal select-none">
            {step === 1
              ? `${AIAssistant.name}'s here to help you on your coding journey! Got a question about the lesson? Just ask ${AIAssistant.name}—they've got the answers. Finished your awesome code? Show it to ${AIAssistant.name}, and they’ll give you a big thumbs-up and mark it complete!`
              : step === 2
              ? "In the Scratch Edit menu, you’ll find a magic button called Restore. If you accidentally delete a sprite, backdrop, costume, or sound, just click Restore, and it’s back like it never left!"
              : step === 3
              ? "The Scratch File menu is where all the magic happens! Save your awesome creations, upload cool templates or files to get started, and download your finished masterpiece to show off to your friends!"
              : step === 4
              ? "Looking for your project list? This button takes you right back to where you started. Don’t forget to save your Scratch code blocks before clicking Back!"
              : step === 5
              ? "Your treasure chest of coding blocks! Each color is a different type of superpower - drag them across to see what they can do."
              : step === 6
              ? "Drag your blocks here and connect them like puzzle pieces. This is where you build your program's brain!"
              : step === 7
              ? "The Stage is where your sprites shine and your code comes alive! It’s like your own magic theater where every action you code happens right in front of your eyes. Watch your ideas come to life instantly—it's showtime!"
              : step === 8
              ? "Pick your characters or create your own. Sprites are the stars of your coding adventure. Get ready to bring them to life!"
              : step === 9
              ? "Dive into learning your way! Flip between watching super cool video lessons and typing your own code. Our awesome instructors are here to guide you every step of the way!"
              : step === 10
              ? "Feeling a little lost? Don’t worry, click on Instructions for a step-by-step guide to become a Scratch coding superstar. Watch the video, follow the steps, and start coding—all on one screen."
              : ""}
          </div>
          <div className="self-stretch justify-start items-center gap-[9px] inline-flex">
            <div className="grow shrink basis-0 h-2 justify-start items-center gap-2 flex">
              {[...Array(10).keys()].map((num: number) => {
                return (
                  <div
                    key={`dot-${num}`}
                    className={`${
                      step === num + 1
                        ? "w-8 bg-neutral-400"
                        : "w-2 bg-neutral-200"
                    } h-2 rounded-xl cursor-pointer`}
                    onClick={handleNavigationButtonsClick(num + 1)}
                  />
                );
              })}
            </div>
            <div
              className="self-stretch px-[18px] py-2.5 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded justify-center items-center gap-2 flex cursor-pointer"
              onClick={handleNextClick}
            >
              <div className="text-white text-base font-semibold font-sans leading-[19px] select-none">
                {step <= 9 ? "Next" : "Start here"}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default OnboardingInstructions;
